/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react'
import { Form, Input, Modal, Select, Switch } from 'antd'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'

import { SubmitUpdateTruckData, Truck, UpdateTruckData } from '@types'
import { useEquipment } from '@hooks'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'

type Props = {
  open: boolean
  onClose: () => void
  onEdit: (data: UpdateTruckData) => void
  data: unknown[]
}

export const validationSchema = yup.object().shape({
  licensePlate: yup.string().required('Campo obligatorio'),
  description: yup.string().required('Campo obligatorio'),
  equipmentType: yup.object().required('Campo obligatorio'),
})

const EditTrailerModal: React.FC<Props> = ({ open, onClose, onEdit, data }) => {
  if (!data || data.length === 0) {
    return null
  }
  const truckData = data[0] as Truck
  let dataSwitch: boolean
  if (truckData?.disableAt === null) {
    dataSwitch = true
  } else {
    dataSwitch = false
  }
  const { t } = useTranslation()
  const { equipments } = useEquipment()
  const [descriptionValue, setDescriptionValue] = useState<string>(truckData?.description)
  const [disableAtSwitch, setDisableAtSwitch] = useState<boolean>(dataSwitch)
  const { reset, handleSubmit, register, setValue, getValues, formState } = useForm<{
    licensePlate: string
    description: string
    equipmentType: { id: unknown }
    disableAt: boolean
  }>({
    resolver: yupResolver(validationSchema),
  })

  useEffect(() => {
    if (truckData && typeof truckData === 'object') {
      const { licensePlate, description, equipmentType, disableAt } = truckData as Truck
      setValue('licensePlate', licensePlate)
      setValue('description', description)
      setValue('disableAt', disableAt === null ? false : dataSwitch)
      if (equipmentType) {
        setValue('equipmentType', { id: equipmentType.id })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckData, setValue])

  const onSubmit = (UpdateData: UpdateTruckData) => {
    if (descriptionValue === '') {
      UpdateData.description = getValues('description')
    } else {
      UpdateData.description = descriptionValue
    }
    UpdateData.disabled = !disableAtSwitch
    onEdit(UpdateData)
    reset()
  }

  return (
    <Modal
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        reset()
        onClose()
      }}
      title={t('tpl_router.tpl_dashboard.edit_trailer_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.edit_truck_modal.button_add')}
      cancelText={t('tpl_router.tpl_dashboard.edit_truck_modal.button_cancel')}
      onOk={handleSubmit(onSubmit as unknown as SubmitHandler<SubmitUpdateTruckData>)}
      destroyOnClose
    >
      <StyledForm>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_truck_modal.licensePlate')}
            name="licensePlate"
          >
            <Input
              {...register('licensePlate')}
              onChange={e => {
                setValue('licensePlate', e.target.value)
              }}
              defaultValue={truckData?.licensePlate}
              disabled
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_truck_modal.description')}
            name="description"
            hasFeedback
            validateStatus={formState.errors.description ? 'error' : ''}
            help={formState.errors.description?.message}
          >
            <Input
              {...register('description')}
              defaultValue={truckData?.description}
              onChange={e => {
                setDescriptionValue(e.target.value)
                setValue('description', e.target.value)
              }}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.edit_truck_modal.equipmentType')}
            name="equipmentType"
            hasFeedback
            validateStatus={formState.errors.equipmentType ? 'error' : ''}
            help={formState.errors.equipmentType?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              options={equipments
                ?.filter(equipment => !equipment.straightTruck)
                .map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
              onChange={value => {
                setValue('equipmentType', { id: value })
              }}
              defaultValue={truckData?.equipmentType?.id}
            />
          </Form.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledRow className="carrier-form-row">
            <Form.Item label="Estado" name="disableAt">
              <Switch
                checked={disableAtSwitch}
                onChange={checked => {
                  setDisableAtSwitch(checked)
                }}
                checkedChildren="HABILITADO"
                unCheckedChildren="DESHABILITADO"
              />
            </Form.Item>
          </StyledRow>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { EditTrailerModal }
